/**
 * `TaskDetail` — the full record + its append-only ledger timeline.
 *
 * The ledger is unbounded, so it is PAGINATED (newest-first): the first page
 * comes with `tasklist.get`, older rows load via "carregar mais antigas"
 * using `historyBefore`, and `historyTotal` sizes the control. A task with a
 * long history never over-fetches.
 *
 * Disambiguation (handoff §2.1): the ULID short suffix + createdAt are shown
 * so two same-title tasks are tellable apart, and `recurrenceOf` renders
 * "recorrente — instância anterior" linking the prior task.
 *
 * `inspection` is backend-internal and is NEVER rendered.
 */
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Repeat, User, Bot } from "lucide-react";

import { cn } from "@/lib/utils";
import type { TaskRow as TaskRecord, UpdateRow } from "@/api/lx-tasklist";
import { StatusChip, PriorityBadge } from "./task-visuals";
import { TaskActions } from "./TaskActions";

export interface TaskDetailProps {
  task: TaskRecord;
  history: UpdateRow[];
  historyTotal: number;
  hasMoreHistory: boolean;
  isLoadingMore: boolean;
  onLoadOlder: () => void;
  onBack: () => void;
  onOpenTask: (taskId: string) => void;
  onChanged: () => void;
}

export function TaskDetail({
  task,
  history,
  historyTotal,
  hasMoreHistory,
  isLoadingMore,
  onLoadOlder,
  onBack,
  onOpenTask,
  onChanged,
}: TaskDetailProps) {
  const { t, i18n } = useTranslation();
  // ULID short suffix — enough to tell same-title instances apart.
  const shortId = task.id.slice(-6);

  return (
    <div className="flex h-full flex-col" data-testid={`task-detail-${task.id}`}>
      <div className="flex items-center gap-2 border-b border-cream-200/60 px-3 py-2 dark:border-coffee-800/60">
        <button
          type="button"
          data-testid="task-detail-back"
          onClick={onBack}
          aria-label={t("common.back")}
          className="rounded p-1 text-slate-400 hover:bg-cream-100 hover:text-slate-600 dark:hover:bg-coffee-800"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
          {task.title}
        </span>
        <StatusChip status={task.status} />
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <div className="flex flex-wrap items-center gap-2">
          <PriorityBadge priority={task.priority} />
          {task.dueDate && (
            <span className="text-[13px] text-slate-500 dark:text-slate-400">
              {t("tasklist.duePrefix")}{" "}
              {new Date(`${task.dueDate}T00:00:00`).toLocaleDateString(i18n.language, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          )}
        </div>

        {task.detail && (
          <p className="mt-2 whitespace-pre-wrap text-[13px] leading-snug text-slate-600 dark:text-slate-300">
            {task.detail}
          </p>
        )}

        {/* recurrenceOf: link to the instance this one descends from. */}
        {task.recurrenceOf && (
          <button
            type="button"
            data-testid="task-recurrence-link"
            onClick={() => onOpenTask(task.recurrenceOf!)}
            className="mt-2 inline-flex items-center gap-1 text-[13px] text-blue-600/90 hover:underline dark:text-blue-400/90"
          >
            <Repeat size={11} aria-hidden />
            {t("tasklist.recurrenceLink")}
          </button>
        )}

        {/* Disambiguation line. */}
        <div className="mt-2 flex flex-wrap gap-x-3 text-[11px] tabular-nums text-slate-400 dark:text-slate-500">
          <span data-testid="task-short-id">#{shortId}</span>
          <span>
            {t("tasklist.createdAt")}{" "}
            {new Date(task.audit.createdAt).toLocaleString(i18n.language)}
          </span>
        </div>

        <TaskActions task={task} onChanged={onChanged} />

        {/* The ledger timeline. */}
        <h3 className="mt-4 mb-1 text-[13px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {t("tasklist.timeline")} ({historyTotal})
        </h3>
        <ol className="space-y-2">
          {history.map((row, i) => (
            <li
              key={`${row.at}-${i}`}
              data-testid="task-timeline-row"
              className="flex gap-2 text-[13px]"
            >
              <span
                className={cn(
                  "mt-0.5 shrink-0",
                  row.by === "agent" ? "text-violet-500/70" : "text-slate-400",
                )}
                title={t(`tasklist.by.${row.by}`)}
              >
                {row.by === "agent" ? <Bot size={11} /> : <User size={11} />}
              </span>
              <div className="min-w-0 flex-1">
                {row.statusChange && (
                  <span
                    data-testid="task-timeline-status-change"
                    className="mr-1 rounded bg-cream-200/60 px-1 py-0.5 text-[11px] uppercase tracking-wide text-slate-500 dark:bg-coffee-700/40 dark:text-slate-400"
                  >
                    {t(`tasklist.status.${row.statusChange.from}`)} →{" "}
                    {t(`tasklist.status.${row.statusChange.to}`)}
                  </span>
                )}
                <span className="text-slate-600 dark:text-slate-300">{row.note}</span>
                <div className="text-[11px] tabular-nums text-slate-400 dark:text-slate-500">
                  {new Date(row.at).toLocaleString(i18n.language)}
                </div>
              </div>
            </li>
          ))}
        </ol>

        {hasMoreHistory && (
          <button
            type="button"
            data-testid="task-timeline-load-older"
            onClick={onLoadOlder}
            disabled={isLoadingMore}
            className="mt-3 inline-flex items-center gap-1.5 rounded border border-cream-200 px-2 py-1 text-[13px] text-slate-500 hover:bg-cream-50 disabled:opacity-50 dark:border-coffee-800 dark:text-slate-400 dark:hover:bg-coffee-900"
          >
            {isLoadingMore && <Loader2 size={11} className="animate-spin" aria-hidden />}
            {t("tasklist.loadOlder")}
          </button>
        )}
      </div>
    </div>
  );
}
